// src/components/charts/BarTopFailureReasons.jsx
//
// Horizontal bar chart of top failure reasons. Sourced from a sample of the
// most-recent failed runs (BE-capped at 200) — NOT from stats. The /runs/stats
// endpoint has no `status` filter and no `error_code` group_by, so the only
// way to count error codes is to enumerate sampled rows.
//
import React from 'react';
import { COLORS, FONT_SANS, SEMANTIC } from '../../constants/theme.js';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';

const TOP_N = 5;

/**
 * Roll up an array of run rows into top-N error-code buckets + "Altri".
 * Input: rows[].error_code (string | null). Null → "Sconosciuto".
 * Exported for unit testing.
 */
export function topErrorBucketsFromRuns(runs, n = TOP_N) {
  if (!Array.isArray(runs) || runs.length === 0) return [];
  const counts = new Map();
  for (const r of runs) {
    const key = r?.error_code == null ? 'Sconosciuto' : r.error_code;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const sorted = [...counts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
  if (sorted.length <= n) return sorted;
  const top = sorted.slice(0, n);
  const restCount = sorted.slice(n).reduce((acc, x) => acc + x.count, 0);
  if (restCount > 0) top.push({ reason: 'Altri', count: restCount });
  return top;
}

// OLD helper kept as an alias for any test still using it.
// @deprecated use topErrorBucketsFromRuns. Will be removed in a follow-up.
export function topErrorBuckets() { return []; }

export default function BarTopFailureReasons({ runs }) {
  const rows = topErrorBucketsFromRuns(runs);
  if (rows.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 36 + 40)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
        <YAxis
          type="category"
          dataKey="reason"
          width={140}
          tick={{ fontSize: 11, fill: COLORS.ink }}
        />
        <Tooltip contentStyle={{ fontSize: 12, fontFamily: FONT_SANS }} />
        <Bar dataKey="count">
          {rows.map((row) => (
            <Cell key={row.reason} fill={row.reason === 'Altri' ? COLORS.textTertiary : SEMANTIC.errore.fg} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
